import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import Button from '../../components/ui/button';
import {
  Book,
  Code,
  Terminal,
  Shield,
  FileText,
  Zap
} from 'lucide-react';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = {
    'getting-started': {
      title: 'Getting Started',
      description: 'Quick guide to integrate LLM Checks into your project',
      steps: [
        'Sign up for an API key',
        'Install the LLM Checks library',
        'Configure your project',
        'Start validating your models'
      ]
    },
    'installation': {
      title: 'Installation',
      description: 'How to install and set up LLM Checks',
      code: `
# Install via npm
npm install llm-checks

# Or using yarn
yarn add llm-checks

# Import in your project
import LLMChecks from 'llm-checks';

# Initialize with your API key
const llmChecks = new LLMChecks('YOUR_API_KEY');
      `
    },
    'usage': {
      title: 'Basic Usage',
      description: 'Simple examples of using LLM Checks',
      code: `
# Validate a website
const result = await llmChecks.validateWebsite('https://llmchecks.com');

# Run security scan
const securityReport = await llmChecks.securityScan(modelOutput);

# Generate detailed insights
const insights = await llmChecks.generateInsights(modelConfig);
      `
    }
  };

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            LLM Checks <span className="text-primary">Documentation</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive guide to integrating and using LLM Checks in your AI projects
          </p>
        </section>

        {/* Documentation Navigation */}
        <section className="max-w-4xl mx-auto mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            {Object.keys(sections).map((key) => (
              <Card
                key={key}
                className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer ${
                  activeSection === key ? 'border-2 border-primary' : ''
                }`}
                onClick={() => setActiveSection(key)}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    {key === 'getting-started' && <Book className="w-6 h-6 text-primary" />}
                    {key === 'installation' && <Terminal className="w-6 h-6 text-blue-500" />}
                    {key === 'usage' && <Code className="w-6 h-6 text-green-500" />}
                  </div>
                  <CardTitle>{sections[key].title}</CardTitle>
                  <CardDescription>{sections[key].description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        {/* Documentation Content */}
        <section className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle>{sections[activeSection].title}</CardTitle>
              <CardDescription>{sections[activeSection].description}</CardDescription>
            </CardHeader>
            <CardContent>
              {activeSection === 'getting-started' && (
                <ul className="space-y-4">
                  {sections[activeSection].steps.map((step, index) => (
                    <li key={index} className="flex items-center">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                        <span className="text-primary font-bold">{index + 1}</span>
                      </div>
                      {step}
                    </li>
                  ))}
                </ul>
              )}

              {(activeSection === 'installation' || activeSection === 'usage') && (
                <div className="bg-gray-900 text-white p-6 rounded-lg">
                  <pre className="text-sm overflow-x-auto">
                    <code>{sections[activeSection].code.trim()}</code>
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Additional Resources */}
        <section className="max-w-4xl mx-auto mt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Additional Resources</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore more ways to leverage LLM Checks in your projects
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'API Reference',
                description: 'Detailed API documentation',
                icon: FileText
              },
              {
                title: 'Security Guide',
                description: 'Best practices for AI security',
                icon: Shield
              },
              {
                title: 'Quick Start',
                description: 'Rapid integration tutorial',
                icon: Zap
              }
            ].map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <resource.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>{resource.title}</CardTitle>
                  <CardDescription>{resource.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mt-16">
          <h2 className="text-3xl font-bold mb-6">Need More Help?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Our support team is ready to assist you with any questions or integration challenges
          </p>
          <Button size="lg">
            Contact Support
          </Button>
        </section>
      </div>
    </div>
  );
};

export default Documentation;
